import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TurfTalk } from "@/api/client";
import { IS_PRELAUNCH } from "@/config/launch";
import { extractResetToken, validateNewPassword, classifyResetError, classifyForgotError, FORGOT_NEUTRAL, MIN_RESET_PASSWORD, MAX_RESET_PASSWORD } from "@/lib/passwordReset";
import { readBridgedInviteCode, decideInvite, inviteReasonMessage } from "@/lib/inviteCode";

function AuthLayout({ children, kicker, title }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B1220]">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden" style={{ background: "linear-gradient(160deg,#0e1b2e 0%,#0b1626 55%,#070d15 100%)" }}>
        {/* subtle game-day cues: yard lines + turf glow */}
        <div aria-hidden className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "repeating-linear-gradient(90deg, #ffffff 0 2px, transparent 2px 96px)" }} />
        <div aria-hidden className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(circle, #2F7D32 0%, transparent 70%)" }} />
        <div className="relative flex items-center gap-5">
          <img src="/tt-mic-cropped.png" alt="Turf Talk" className="h-[104px] w-auto object-contain" data-testid="landing-brand-logo" />
          <div className="flex flex-col">
            <span className="font-display font-bold text-[48px] leading-none tracking-wide text-white" data-testid="landing-brand-wordmark">TURF TALK</span>
            <span className="font-semibold text-[15px] tracking-[0.02em] text-white/85 mt-1.5" data-testid="landing-brand-subline">by SportChant</span>
          </div>
        </div>
        <div className="relative">
          <div className="text-[11px] tracking-[0.22em] text-[#4ea152] font-display mb-3">LIVE NFL + COLLEGE FOOTBALL</div>
          <h2 className="font-display text-5xl xl:text-6xl text-white leading-[0.95]">Every game has a<br />conversation.<br /><span className="text-[#4ea152]">Join it.</span></h2>
          <p className="text-white/60 mt-5 max-w-md leading-relaxed">Follow NFL and college football, jump into live game rooms, talk with friends, make picks, compete in rivalry leagues, plan tailgates, and catch the Turf Talk Character Show—all in one place.</p>
        </div>
        <div className="relative text-white/40 text-xs">A SportChant experience · Designed and built by Titan Haven Group</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md tt-rise">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/tt-mic-cropped.png" alt="Turf Talk" className="h-14 w-auto object-contain" />
            <div className="flex flex-col">
              <span className="font-display text-2xl leading-none text-white">TURF TALK</span>
              <span className="font-semibold text-xs tracking-[0.02em] text-white/85 mt-1">by SportChant</span>
            </div>
          </div>
          {kicker && <div className="text-[11px] tracking-[0.2em] text-[#4ea152] font-display mb-1">{kicker}</div>}
          <h1 className="font-display text-4xl text-white mb-6">{title}</h1>
          {children}
          <div className="lg:hidden mt-10 text-white/30 text-xs">A SportChant experience · Designed and built by Titan Haven Group</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block mb-4">
      <span className="block text-sm text-white/70 mb-1.5">{label}</span>
      <input {...props} className="w-full h-11 bg-[#131a24] border border-[#2A3544] rounded-lg px-3 text-white placeholder:text-white/30 focus:border-[#2F7D32] outline-none transition-colors" />
    </label>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const me = await login(email, password);
      toast.success("Welcome back to Turf Talk");
      navigate(me?.onboarding_complete === false ? "/onboarding" : next);
    } catch (e) { setErr(e.message || "Login failed"); } finally { setBusy(false); }
  };

  return (
    <AuthLayout kicker="WELCOME BACK" title="Sign in to join the conversation">
      <form onSubmit={submit} data-testid="login-form">
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" />
        <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
        {err && <div className="text-[#ff6b60] text-sm mb-4" data-testid="login-error">{err}</div>}
        <button disabled={busy} data-testid="login-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors disabled:opacity-50">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div className="mt-3">
        <Link to="/forgot" className="text-white/50 hover:text-[#4ea152] text-sm transition-colors" data-testid="to-forgot">Forgot your password?</Link>
      </div>
      {IS_PRELAUNCH ? (
        <p className="text-white/50 text-sm mt-5" data-testid="login-invite-note">Turf Talk is in closed beta. <a href="mailto:operations@titanhavengroup.com?subject=Turf%20Talk%20Demo%20Request" className="text-[#4ea152] hover:underline" data-testid="login-request-demo">Request a demo</a></p>
      ) : (
        <p className="text-white/50 text-sm mt-5">New here? <Link to="/register" className="text-[#4ea152] hover:underline" data-testid="to-register">Create an account</Link></p>
      )}
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!email.includes("@")) { setErr("Enter a valid email address."); return; }
    setBusy(true);
    try {
      await TurfTalk.forgotPassword(email.trim());
      setSent(true); // neutral regardless of whether the account exists
    } catch (e2) {
      const c = classifyForgotError(e2);
      if (c.retry) setErr(c.message);
      else setSent(true); // account-existence errors never leak → stay neutral
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout kicker="ACCOUNT RECOVERY" title="Reset your password">
      {sent ? (
        <div data-testid="forgot-sent">
          <div className="rounded-lg border border-[#24362A] bg-[#122017] px-4 py-3.5 text-sm text-white/80 leading-relaxed mb-6">{FORGOT_NEUTRAL}</div>
          <Link to="/login" className="inline-block h-11 leading-[2.75rem] px-5 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors" data-testid="forgot-back-login">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <p className="text-white/50 text-sm mb-5">Enter the email on your account and we'll send a link to reset your password.</p>
          <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" autoComplete="email" />
          {err && <div className="text-[#ff6b60] text-sm mb-4" data-testid="forgot-error">{err}</div>}
          <button disabled={busy} data-testid="forgot-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors disabled:opacity-50">
            {busy ? "Sending…" : "Send reset link"}
          </button>
          <p className="text-white/50 text-sm mt-5">Remembered it? <Link to="/login" className="text-[#4ea152] hover:underline" data-testid="forgot-to-login">Sign in</Link></p>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const tokenRef = useRef(null);
  const [hasToken, setHasToken] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [outcome, setOutcome] = useState(null); // null | "done" | {kind,message,canRequestNew}

  useEffect(() => {
    // Capture the token ONCE (never auto-consumed — reset is POST on submit).
    // Priority: the pre-bootstrap bridge (full page load, already scrubbed from
    // the URL) → router query (SPA entry) → raw location. Then clear the bridge
    // and scrub the address bar so it can't leak via history/Referer/analytics.
    const bridged = typeof window !== "undefined" ? window.__TT_RESET_TOKEN__ : null;
    const t = bridged || params.get("token") || extractResetToken(window.location.search);
    if (t) { tokenRef.current = t; setHasToken(true); }
    try { if (typeof window !== "undefined") delete window.__TT_RESET_TOKEN__; } catch { /* noop */ }
    const meta = document.createElement("meta");
    meta.name = "referrer"; meta.content = "no-referrer";
    document.head.appendChild(meta);
    if (t) { try { window.history.replaceState({}, document.title, "/reset"); } catch { /* noop */ } }
    return () => { try { document.head.removeChild(meta); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const v = validateNewPassword(pw, confirm);
    if (v) { setErr(v); return; }
    if (!tokenRef.current) { setErr("This reset link is invalid."); return; }
    setBusy(true);
    try {
      await TurfTalk.resetPassword(tokenRef.current, pw); // token/password never logged or tracked
      setPw(""); setConfirm(""); tokenRef.current = null;
      setOutcome("done");
    } catch (e2) {
      const c = classifyResetError(e2);
      setOutcome(c); setErr(c.message);
    } finally { setBusy(false); }
  };

  if (outcome === "done") {
    return (
      <AuthLayout kicker="ACCOUNT RECOVERY" title="Password updated">
        <div data-testid="reset-success">
          <div className="rounded-lg border border-[#24362A] bg-[#122017] px-4 py-3.5 text-sm text-white/80 leading-relaxed mb-6">Your password has been reset. You can now sign in with your new password.</div>
          <Link to="/login" className="inline-block h-11 leading-[2.75rem] px-5 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors" data-testid="reset-to-login">Continue to sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  const requestNew = !hasToken || (outcome && outcome.canRequestNew);

  return (
    <AuthLayout kicker="ACCOUNT RECOVERY" title="Choose a new password">
      {requestNew ? (
        <div data-testid="reset-link-problem">
          <div className="rounded-lg border border-[#3a2f14] bg-[#241d0e] px-4 py-3.5 text-sm text-[#F4B740] leading-relaxed mb-6">
            {!hasToken ? "This password reset link is missing or invalid." : (outcome && outcome.message)}
          </div>
          <Link to="/forgot" className="inline-block h-11 leading-[2.75rem] px-5 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors" data-testid="reset-request-new">Request a new link</Link>
          <p className="text-white/50 text-sm mt-5"><Link to="/login" className="text-[#4ea152] hover:underline" data-testid="reset-to-login-2">Back to sign in</Link></p>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="reset-form">
          <p className="text-white/50 text-sm mb-5">Enter a new password for your account. Minimum {MIN_RESET_PASSWORD} characters.</p>
          <Field label="New password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" autoComplete="new-password" placeholder={`${MIN_RESET_PASSWORD}+ characters`} maxLength={MAX_RESET_PASSWORD} />
          <Field label="Confirm new password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" autoComplete="new-password" maxLength={MAX_RESET_PASSWORD} />
          {err && <div className="text-[#ff6b60] text-sm mb-4" data-testid="reset-error">{err}</div>}
          <button disabled={busy} data-testid="reset-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors disabled:opacity-50">
            {busy ? "Updating…" : "Update password"}
          </button>
          <p className="text-white/50 text-sm mt-5"><Link to="/login" className="text-[#4ea152] hover:underline" data-testid="reset-to-login">Back to sign in</Link></p>
        </form>
      )}
    </AuthLayout>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  // The invite code is held ONLY in a ref (never state/storage/logs) so it is
  // never rendered, never serialized into telemetry, and gone on a hard reload.
  const codeRef = useRef("");
  const ran = useRef(false);
  const [invitePhase, setInvitePhase] = useState("checking"); // checking | ready | blocked
  const [blockMsg, setBlockMsg] = useState("");
  const [f, setF] = useState({ email: "", password: "", handle: "", display_name: "" });
  const [acks, setAcks] = useState({ age: false, terms: false, privacy: false, guidelines: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    if (ran.current) return; ran.current = true;
    // Referrer protection for this invite-bearing load.
    const meta = document.createElement("meta");
    meta.name = "referrer"; meta.content = "no-referrer";
    document.head.appendChild(meta);
    // Capture the service-issued code from the pre-bootstrap bridge or the live
    // fragment, then scrub BOTH the bridged global and the address bar so the
    // code can never leak via history/Referer/analytics/debug logs.
    const code = readBridgedInviteCode(window);
    codeRef.current = code;
    try { if (typeof window !== "undefined") delete window.__TT_INVITE_CODE__; } catch { /* noop */ }
    try { window.history.replaceState({}, document.title, window.location.pathname + window.location.search); } catch { /* noop */ }
    let alive = true;
    (async () => {
      if (!code) { if (alive) { setInvitePhase("blocked"); setBlockMsg(inviteReasonMessage("missing")); } return; }
      try {
        const resp = await TurfTalk.validateInvite(code); // non-consuming, server-authoritative
        const d = decideInvite(resp);
        if (!alive) return;
        if (d.allowed) setInvitePhase("ready");
        else { setInvitePhase("blocked"); setBlockMsg(d.message); }
      } catch {
        // NEVER default-allow: any error / rate-limit / network failure blocks.
        if (!alive) return;
        setInvitePhase("blocked"); setBlockMsg(inviteReasonMessage("unknown"));
      }
    })();
    return () => { alive = false; try { document.head.removeChild(meta); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (invitePhase !== "ready" || !codeRef.current) { setErr("A valid invite is required to create an account."); return; }
    if (!acks.age || !acks.terms || !acks.privacy || !acks.guidelines) { setErr("You must be 18+ and accept the policies."); return; }
    setBusy(true);
    try {
      // Normal-user role is backend-enforced (no is_admin sent). Password is the
      // registrant's own; email verification is handled by the backend post-create.
      const me = await register({
        email: f.email, password: f.password, handle: f.handle, display_name: f.display_name,
        age_attested: true, accepted_terms: true, accepted_privacy: true, accepted_guidelines: true,
        beta_code: codeRef.current, time_zone: tz,
      });
      toast.success("Account created");
      navigate(me?.onboarding_complete ? "/" : "/onboarding");
    } catch (e2) { setErr(e2.message || "Registration failed"); } finally { setBusy(false); }
  };

  const Ack = ({ k, children }) => (
    <label className="flex items-start gap-2 text-sm text-white/70 mb-2 cursor-pointer">
      <input type="checkbox" checked={acks[k]} onChange={(e) => setAcks((s) => ({ ...s, [k]: e.target.checked }))} data-testid={`ack-${k}`} className="mt-0.5 accent-[#2F7D32]" />
      <span>{children}</span>
    </label>
  );

  if (invitePhase === "checking") {
    return (
      <AuthLayout kicker="JOIN THE BROADCAST" title="Checking your invite">
        <div data-testid="register-invite-checking" className="rounded-lg border border-[#24362A] bg-[#122017] px-4 py-3.5 text-sm text-white/80 animate-pulse">Validating your invite…</div>
      </AuthLayout>
    );
  }

  if (invitePhase === "blocked") {
    return (
      <AuthLayout kicker="JOIN THE BROADCAST" title="Invite needed">
        <div data-testid="register-invite-blocked">
          <div className="rounded-lg border border-[#3a2f14] bg-[#241d0e] px-4 py-3.5 text-sm text-[#F4B740] leading-relaxed mb-6" data-testid="register-invite-message">{blockMsg || inviteReasonMessage("unknown")}</div>
          <p className="text-white/50 text-sm">Turf Talk is in closed beta. <a href="mailto:operations@titanhavengroup.com?subject=Turf%20Talk%20Invite%20Request" className="text-[#4ea152] hover:underline" data-testid="register-request-invite">Request an invite</a></p>
          <p className="text-white/50 text-sm mt-3">Already have an account? <Link to="/login" className="text-[#4ea152] hover:underline" data-testid="to-login">Sign in</Link></p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout kicker="JOIN THE BROADCAST" title="Create account">
      <div className="rounded-lg border border-[#24362A] bg-[#122017] px-3 py-2 text-[12px] text-[#4ea152] mb-4" data-testid="register-invite-verified">Invite verified — finish creating your account.</div>
      <form onSubmit={submit} data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Display name" value={f.display_name} onChange={set("display_name")} data-testid="reg-display" />
          <Field label="Handle" value={f.handle} onChange={set("handle")} placeholder="lowercase_only" data-testid="reg-handle" />
        </div>
        <Field label="Email" type="email" value={f.email} onChange={set("email")} data-testid="reg-email" />
        <Field label="Password" type="password" value={f.password} onChange={set("password")} placeholder="8+ characters" data-testid="reg-password" />
        <div className="mt-2 mb-4">
          <Ack k="age">I confirm I am 18 years or older.</Ack>
          <Ack k="terms">I accept the Terms of Service.</Ack>
          <Ack k="privacy">I accept the Privacy Policy.</Ack>
          <Ack k="guidelines">I accept the Community Guidelines.</Ack>
        </div>
        {err && <div className="text-[#ff6b60] text-sm mb-4" data-testid="register-error">{err}</div>}
        <button disabled={busy} data-testid="register-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors disabled:opacity-50">
          {busy ? "Creating…" : "Create account"}
        </button>
      </form>
      <p className="text-white/50 text-sm mt-5">Already have an account? <Link to="/login" className="text-[#4ea152] hover:underline" data-testid="to-login">Sign in</Link></p>
    </AuthLayout>
  );
}

export function Onboarding() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState({ NFL: [], NCAA: [] });
  const [nfl, setNfl] = useState([]);
  const [ncaa, setNcaa] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([TurfTalk.teams("NFL"), TurfTalk.teams("NCAA")]).then(([a, b]) => {
      setTeams({ NFL: a.teams || a || [], NCAA: b.teams || b || [] });
    }).catch(() => {});
  }, []);

  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const finish = async () => {
    setBusy(true);
    try {
      await TurfTalk.completeOnboarding({ followed_nfl_team_ids: nfl, followed_ncaa_team_ids: ncaa, public_profile: true, time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" });
      await refreshMe();
      toast.success("You're all set");
      navigate("/");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const TeamChips = ({ list, sel, onToggle }) => (
    <div className="flex flex-wrap gap-2">
      {list.map((t) => {
        const on = sel.includes(t.id);
        return (
          <button key={t.id} onClick={() => onToggle(t.id)} data-testid={`team-${t.id}`}
            className={`px-3 h-9 rounded-full text-sm border transition-colors ${on ? "bg-[#24362A] border-[#2F7D32] text-white" : "border-[#2A3544] text-white/60 hover:text-white"}`}>
            {t.abbr} · {t.name}
          </button>
        );
      })}
    </div>
  );

  return (
    <AuthLayout kicker="PERSONALIZE" title="Follow your teams">
      <p className="text-white/50 text-sm mb-5">Pick the teams you follow. We'll surface their games, rooms and kickoff alerts first.</p>
      <div className="mb-5">
        <div className="font-display tracking-widest text-xs text-white/50 mb-2">NFL</div>
        <TeamChips list={teams.NFL} sel={nfl} onToggle={(id) => toggle(nfl, setNfl, id)} />
      </div>
      <div className="mb-6">
        <div className="font-display tracking-widest text-xs text-white/50 mb-2">NCAA</div>
        <TeamChips list={teams.NCAA} sel={ncaa} onToggle={(id) => toggle(ncaa, setNcaa, id)} />
      </div>
      <button disabled={busy} onClick={finish} data-testid="onboarding-finish" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors disabled:opacity-50">
        {busy ? "Saving…" : "Enter Turf Talk"}
      </button>
    </AuthLayout>
  );
}
